import React, { useCallback, useEffect, useState } from 'react';
import axios from '../api/axiosConfig';

// Alerta de "Mis Trámites": placas ya pre-asignadas por el tránsito cuyo FUN y contrato de mandato diligenciados
// el concesionario aún no ha descargado (para imprimirlos y que el asesor los firme).
const DOCS = [['fun', 'FUN', '📄'], ['contrato', 'Contrato de mandato', '📝']];

const AlertaDocumentosPreasignacion = () => {
  const [pendientes, setPendientes] = useState([]);
  const [abierto, setAbierto] = useState(true);
  const [ocupado, setOcupado] = useState(null); // `${id}-${tipo}` o 'todo'
  const [error, setError] = useState('');

  const cargar = useCallback(async () => {
    try {
      const res = await axios.get('/api/tramites/documentos-preasignacion/pendientes');
      setPendientes(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      // La alerta es informativa: si falla no se interrumpe Mis Trámites
      setPendientes([]);
    }
  }, []);

  useEffect(() => {
    cargar();
    const t = setInterval(cargar, 60000);
    return () => clearInterval(t);
  }, [cargar]);

  const descargar = async (p, tipo) => {
    const res = await axios.get(`/api/tramites/${p.id}/documento-preasignacion/${tipo}`, { responseType: 'blob' });
    const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `${tipo === 'fun' ? 'FUN' : 'CONTRATO_MANDATO'}_${p.placa || 'TRAMITE_' + p.id}.pdf`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => window.URL.revokeObjectURL(url), 5000);
  };

  const descargarUno = async (p, tipo) => {
    setOcupado(`${p.id}-${tipo}`); setError('');
    try { await descargar(p, tipo); await cargar(); }
    catch (e) { setError(`No se pudo descargar el documento de ${p.placa || '#' + p.id}.`); }
    finally { setOcupado(null); }
  };

  const descargarTodo = async () => {
    setOcupado('todo'); setError('');
    let fallos = 0;
    for (const p of pendientes) {
      for (const [tipo] of DOCS) {
        if (p[`${tipo}_descargado`]) continue;
        try { await descargar(p, tipo); await new Promise(r => setTimeout(r, 400)); } catch (e) { fallos++; }
      }
    }
    if (fallos) setError(`${fallos} documento(s) no se pudieron descargar. Intente de nuevo.`);
    await cargar();
    setOcupado(null);
  };

  if (pendientes.length === 0) return null;

  return (
    <div role="alert" style={{ background: '#fffbeb', border: '1px solid #f59e0b', borderLeft: '6px solid #f59e0b', borderRadius: '12px', padding: '14px 18px', marginBottom: '18px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
        <div>
          <strong style={{ color: '#92400e', fontSize: '15px' }}>
            📥 {pendientes.length === 1 ? '1 placa fue pre-asignada' : `${pendientes.length} placas fueron pre-asignadas`}: descargue el FUN y el contrato de mandato
          </strong>
          <div style={{ fontSize: '12px', color: '#a16207', marginTop: '2px' }}>
            Imprímalos y consiga la firma del asesor/propietario. Esta alerta desaparece cuando descargue ambos documentos de cada placa.
          </div>
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button type="button" onClick={descargarTodo} disabled={ocupado !== null}
            style={{ padding: '8px 14px', background: '#d97706', color: '#fff', border: 'none', borderRadius: '8px', fontWeight: 800, cursor: 'pointer', fontSize: '13px' }}>
            {ocupado === 'todo' ? '⏳ Descargando...' : '⬇️ Descargar todo'}
          </button>
          <button type="button" onClick={() => setAbierto(a => !a)} aria-expanded={abierto}
            style={{ padding: '8px 12px', background: '#fef3c7', color: '#92400e', border: '1px solid #f59e0b', borderRadius: '8px', fontWeight: 700, cursor: 'pointer', fontSize: '13px' }}>
            {abierto ? 'Ocultar lista' : 'Ver lista'}
          </button>
        </div>
      </div>

      {error && <div style={{ marginTop: '8px', color: '#b91c1c', fontSize: '12px', fontWeight: 700 }}>{error}</div>}

      {abierto && (
        <div style={{ marginTop: '12px', display: 'flex', flexDirection: 'column', gap: '8px', maxHeight: '320px', overflowY: 'auto' }}>
          {pendientes.map(p => (
            <div key={p.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px', background: '#fff', border: '1px solid #fde68a', borderRadius: '10px', padding: '8px 12px' }}>
              <div style={{ minWidth: 0 }}>
                <span className="plate-badge-sm">{p.placa || `#${p.id}`}</span>
                <span style={{ marginLeft: '8px', fontSize: '13px', fontWeight: 600, color: '#334155' }}>{p.propietario || '—'}</span>
                <div style={{ fontSize: '11px', color: '#64748b' }}>Trámite #{p.id} · {p.tipo || '—'} · {p.transito || '—'}</div>
              </div>
              <div style={{ display: 'flex', gap: '6px' }}>
                {DOCS.map(([tipo, label, icono]) => {
                  const hecho = p[`${tipo}_descargado`];
                  return (
                    <button key={tipo} type="button" onClick={() => descargarUno(p, tipo)} disabled={ocupado !== null}
                      title={hecho ? 'Ya descargado (puede volver a descargarlo)' : 'Descargar'}
                      style={{ padding: '6px 10px', fontSize: '12px', fontWeight: 700, borderRadius: '8px', cursor: 'pointer', border: 'none',
                        background: hecho ? '#dcfce7' : '#2563eb', color: hecho ? '#166534' : '#fff' }}>
                      {ocupado === `${p.id}-${tipo}` ? '⏳' : hecho ? `✅ ${label}` : `${icono} ${label}`}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AlertaDocumentosPreasignacion;
